/**
 * Task timer API (Start / Pause / Resume / Stop).
 *
 * A timer lives on the server, so it survives a refresh and shows up on other
 * devices. Stopping converts the session into a normal worklog TimeEntry —
 * this is not a second time-tracking system.
 */
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TimeEntry } from './tasksApi';

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface ActiveTimer {
  id: string;
  taskId: string;
  taskTitle: string | null;
  taskIdentifier: string | null;
  /** First start (ISO) — the "started at" shown in the bar, never reset by a pause. */
  startedAt: string;
  /** ISO while running, null while paused. */
  runningSince: string | null;
  /** Seconds banked from the segments before the last pause. */
  accumulatedSeconds: number;
}

export interface ActiveTimersResult {
  timers: ActiveTimer[];
  /** Server clock at response time, used to correct the client's own drift. */
  serverNow: string;
}

export async function listActiveTimers(): Promise<ActiveTimersResult> {
  const res = await apiFetch<Envelope<ActiveTimersResult>>('/api/tasks/timers/active', {
    headers: authHeaders(),
  });
  // Test Mode's generic fallback has no `timers` key — read defensively so a
  // missing mock route cannot blank every page.
  return {
    timers: res.data?.timers ?? [],
    serverNow: res.data?.serverNow ?? new Date().toISOString(),
  };
}

/** One person's live timer on a task, as anyone looking at that task sees it. */
export interface TaskLiveTimer {
  id: string;
  user: { id: string; name: string };
  startedAt: string;
  runningSince: string | null;
  accumulatedSeconds: number;
}

export interface TaskLiveTimersResult {
  timers: TaskLiveTimer[];
  serverNow: string;
}

export async function listTaskLiveTimers(taskId: string): Promise<TaskLiveTimersResult> {
  const res = await apiFetch<Envelope<TaskLiveTimersResult>>(`/api/tasks/${taskId}/timers`, {
    headers: authHeaders(),
  });
  // Read defensively, like listActiveTimers: Test Mode has no such route.
  return {
    timers: res.data?.timers ?? [],
    serverNow: res.data?.serverNow ?? new Date().toISOString(),
  };
}

async function post<T>(taskId: string, action: string): Promise<T> {
  const res = await apiFetch<Envelope<T>>(`/api/tasks/${taskId}/timer/${action}`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

export function startTimer(taskId: string): Promise<{ timer: ActiveTimer }> {
  return post<{ timer: ActiveTimer }>(taskId, 'start');
}

export function pauseTimer(taskId: string): Promise<{ timer: ActiveTimer }> {
  return post<{ timer: ActiveTimer }>(taskId, 'pause');
}

export function resumeTimer(taskId: string): Promise<{ timer: ActiveTimer }> {
  return post<{ timer: ActiveTimer }>(taskId, 'resume');
}

export function stopTimer(taskId: string): Promise<{ stopped: boolean; entry: TimeEntry | null }> {
  return post<{ stopped: boolean; entry: TimeEntry | null }>(taskId, 'stop');
}
